/**
 * declared-change-type-prefill — pure mappers for the interest-target picker.
 *
 * Kept separate from the React component so the normalisation and label logic
 * is unit-testable without a DOM, mirroring `policyPrefillOptions.ts`.
 *
 * The substrate prefill emits `interest_options` as a freeform key (not part of
 * the input schema), so callers must type-narrow before use — same contract as
 * `policy_options`.
 */

import {
  findPolicyOption,
  readPolicyOptions,
  readPolicyTermKey,
} from "./policyPrefillOptions";
import { actionOf } from "@product-app/common/workflows/launch/actionSlice";

/**
 * Read a key that the v2 launch contract moved inside `actions[0]`.
 *
 * The nested read wins whenever the action HAS the key — including when it
 * holds `null`, which is a deliberate "cleared", not "absent". The flat read is
 * the fallback for a pre-reshape value object (legacy fixtures, and any caller
 * that never nested).
 *
 * `policy_options`, `policy_term_key` and `interest_options*` are NOT read
 * through this: they are freeform form-state keys, never schema properties, so
 * they stay flat and `readPolicyOptions` / `readPolicyTermKey` keep reading
 * `values` directly.
 */
function readMovedKey(
  values: Record<string, unknown> | undefined,
  key: string,
): unknown {
  const nested = actionOf(values)[key];
  return nested !== undefined ? nested : values?.[key];
}

function readMovedPolicyNumber(
  values: Record<string, unknown> | undefined,
): string | null {
  const raw = readMovedKey(values, "policy_number");
  return typeof raw === "string" ? raw : null;
}

/** One additional interest as the substrate prefill emits it (camelCase). */
export interface InterestOption {
  interest_id: string;
  name: string;
  loan_number: string;
  address1: string;
  city: string;
  state_code: string;
  postal_code: string;
}

/**
 * Normalize one raw `interest_options` row to the SNAKE_CASE shape the launch
 * model accepts.
 *
 * ⚠️ This translation is load-bearing, not cosmetic. The substrate reader emits
 * camelCase (`interestId`, `loanNumber`, `stateCode`, `postalCode`) while
 * `CarrierMortgageeUpdateInput.target_interest` is snake_case with
 * `populate_by_name=True` and NO alias generator — so it accepts snake_case
 * only. Four of the seven names differ; three (`name`, `address1`, `city`)
 * collide harmlessly. Writing a raw row straight through `setValue` would
 * silently blank four of the five fields the EPIC-side match compares on, and
 * because every one of them defaults to `""` server-side, nothing would error —
 * the run would just quietly fail to match and fall back to a HITL.
 *
 * Accepts BOTH shapes for the same reason `normalizePolicyOption` does: the
 * camelCase shape previously failed a `"policy_number" in x` filter and the
 * policy dropdown silently never rendered.
 */
export function normalizeInterestOption(raw: unknown): InterestOption | null {
  if (typeof raw !== "object" || raw === null) return null;
  const r = raw as Record<string, unknown>;
  const str = (...keys: string[]): string => {
    for (const k of keys) {
      const v = r[k];
      if (typeof v === "string" && v) return v;
    }
    return "";
  };
  const id = str("interest_id", "interestId");
  const name = str("name");
  const address1 = str("address1");
  // A row identifiable by nothing is not a choice a human can make — and this
  // picker leads to a delete. Measured on the warehouse: name blank on 0.62% of
  // rows, loan number on 28.0%.
  if (!id && !name && !address1) return null;
  return {
    interest_id: id,
    name,
    loan_number: str("loan_number", "loanNumber"),
    address1,
    city: str("city"),
    state_code: str("state_code", "stateCode"),
    postal_code: str("postal_code", "postalCode"),
  };
}

/** Narrow `prefill_values.interest_options` to a typed list. */
export function readInterestOptions(
  prefillValues: Record<string, unknown> | undefined,
): InterestOption[] {
  const raw = prefillValues?.interest_options;
  if (!Array.isArray(raw)) return [];
  return raw
    .map(normalizeInterestOption)
    .filter((x): x is InterestOption => x !== null);
}

/**
 * Label for one option: mortgagee name plus loan number.
 *
 * Never rank: `RankNumber` is measured NON-unique per line (policy H676899
 * carries two interests both at rank 1), so it can neither order nor
 * disambiguate. When the loan number is blank — 28% of rows — fall back to the
 * address so the choice is still identifiable.
 */
export function interestOptionLabel(o: InterestOption): string {
  const name = o.name || "(unnamed interest)";
  if (o.loan_number) return `${name} — loan ${o.loan_number}`;
  const where = [o.address1, o.city, o.state_code].filter(Boolean).join(", ");
  return where ? `${name} — ${where}` : name;
}

/**
 * Do the server-scoped `interest_options` still describe the SELECTED policy?
 *
 * The substrate scopes `interest_options` to ONE policy line and names it in
 * `interest_options_line_sk`. If the AM then switches policies, those options
 * describe a different line and the picker must not offer a stale target — so
 * this predicate gates it.
 *
 * ⚠️ The selected line MUST be resolved with `findPolicyOption` (term key
 * first, then `default_inforce_term` precedence), NOT by policy number alone.
 * A number is not an identity: a current term and its future renewal share one,
 * and `policy_options` arrives expiration-DESC, so a bare
 * `.find(p => p.policy_number === n)` returns the RENEWAL while the substrate
 * scoped the options to the CURRENT term. The two line_sks then differ, this
 * returns false, and `old_mortgagee_name` silently degrades to free text for
 * every two-term client — which is exactly the bug this function was extracted
 * to make testable. See `policyTermKey`'s note in `policyPrefillOptions.ts`.
 *
 * Fail OPEN: an absent `interest_options_line_sk` (older prefill payloads) or an
 * unresolvable selection means "no evidence of a mismatch", so the picker still
 * shows. Only a KNOWN disagreement suppresses it.
 */
export function interestOptionsMatchSelection(
  values: Record<string, unknown> | undefined,
  today?: Date,
): boolean {
  const interestLineSk = values?.interest_options_line_sk;
  if (interestLineSk === undefined || interestLineSk === null) return true;
  const selected = findPolicyOption(
    readPolicyOptions(values),
    readMovedPolicyNumber(values),
    readPolicyTermKey(values),
    today,
  );
  const selectedLineSk = selected?.policy_line_sk ?? null;
  if (selectedLineSk === null) return true;
  return interestLineSk === selectedLineSk;
}

/**
 * Resolve the selected policy line, then the interests that belong to IT.
 *
 * `interestOptionsMatchSelection` above could only answer "do the one line's
 * options describe what the AM picked?" — and when the answer was no, the field
 * degraded to free text. That is right when the substrate has nothing else to
 * offer, but wrong whenever the other term carries its own mortgagee, which is
 * the common case for a policy covering a current term and its renewal.
 *
 * The substrate now also emits `interest_options_by_line` — every selectable
 * line's options, keyed by line — so the correct answer is to look up the term
 * the AM actually picked instead of comparing against a single line.
 *
 * Precedence, and why:
 *
 *  1. `interest_options_by_line` present → authoritative. A line PRESENT in the
 *     map yields its options; a line ABSENT yields none, because the substrate
 *     emits the map only on a successful read and omits lines with nothing
 *     offerable. Absent therefore means "this term has no mortgagee to pick",
 *     which is a definitive empty, not missing data.
 *  2. Map absent → the substrate predates this key (the add-in and substrate
 *     deploy independently, so this is a live configuration, not a legacy
 *     hypothetical). Fall back to the single-line behaviour exactly as before.
 *
 * Returns `[]` rather than throwing on any malformed shape: this is freeform
 * prefill data, and a launch must never be blocked by a payload surprise.
 */
export function readInterestOptionsForSelection(
  values: Record<string, unknown> | undefined,
  today?: Date,
): InterestOption[] {
  const byLine = values?.interest_options_by_line;
  if (byLine && typeof byLine === "object" && !Array.isArray(byLine)) {
    const selected = findPolicyOption(
      readPolicyOptions(values),
      readMovedPolicyNumber(values),
      readPolicyTermKey(values),
      today,
    );
    const sk = selected?.policy_line_sk ?? null;
    // An unresolvable selection is not evidence about any line, so fall through
    // to the single-line path rather than asserting "no options".
    //
    // `> 0`, not `!== null`: `normalizePolicyOption` defaults a MISSING
    // policy_line_sk to 0 (see `policyTermKey`, which folds the term dates in
    // for exactly that reason), so 0 means "this option has no line identity"
    // — not "line zero". Treating it as resolved would look up a key that
    // cannot exist and return a definitive empty, i.e. fail CLOSED on a field
    // whose whole purpose is not to be free text.
    if (sk !== null && sk > 0) {
      const raw = (byLine as Record<string, unknown>)[String(sk)];
      if (!Array.isArray(raw)) return [];
      return raw
        .map(normalizeInterestOption)
        .filter((x): x is InterestOption => x !== null);
    }
  }
  return interestOptionsMatchSelection(values, today)
    ? readInterestOptions(values)
    : [];
}

/**
 * The interest-scoped values to reset when the POLICY selection changes.
 *
 * Interests are per-term, so a target resolved against one term cannot describe
 * another. Clearing `target_interest` alone is not enough:
 *
 * `old_mortgagee_name` is frequently **derived, not extracted**. The substrate
 * prefill overwrites it with the resolved target's EPIC spelling whenever a
 * target resolves, and an in-form pick writes both keys together. So after a
 * term switch it holds the PREVIOUS term's mortgagee name — and with the target
 * cleared the control renders "Select the existing mortgagee…", which hides that
 * stale value while it still rides to the launch. The field is optional in the
 * launch model, so nothing gates it: the UI would assert "nothing selected"
 * while the wire carried another term's mortgagee.
 *
 * ⚠️ Cleared ONLY when it matches the outgoing target's name, i.e. only when it
 * was derived. A name the AM typed via "Not listed", or one extraction produced
 * that never resolved to a row, is theirs and must survive a policy change —
 * blanking it would be its own data loss on a field the AM may have just filled
 * in by hand.
 */
export function interestResetPatch(
  values: Record<string, unknown> | undefined,
): Record<string, unknown> {
  const target = readMovedKey(values, "target_interest") as
    | InterestOption
    | null
    | undefined;
  // Run-level, NOT moved — `old_mortgagee_name` stayed on the envelope.
  const name = values?.old_mortgagee_name;
  const derived =
    !!target &&
    typeof name === "string" &&
    name.length > 0 &&
    name === target.name;
  return derived
    ? { target_interest: null, old_mortgagee_name: "" }
    : { target_interest: null };
}

/**
 * Pre-select the switched-to term's mortgagee when there is exactly ONE.
 *
 * This mirrors the server's own rule rather than inventing one:
 * `resolve_target` pre-selects the target for a single labelled option
 * (state "auto") and only refuses to choose at TWO or more. Without this,
 * the term the prefill happened to resolve arrives pre-selected while an
 * equivalent single-option sibling term arrives blank after a switch —
 * same data, different UX, decided by which term the server landed on.
 *
 * Callers pass the NEXT values (reset patch + new policy selection already
 * applied): the lone option must resolve against the term the AM just
 * picked, not the outgoing one.
 *
 * Two refusals, both deliberate:
 *  - No policy selection → no patch. The picker is hidden without one, and
 *    a target written while hidden would ride to the launch unseen — the
 *    exact class of invisible-value bug `interestResetPatch` exists to
 *    prevent.
 *  - Anything already filled → no patch. An `old_mortgagee_name` that
 *    survived the reset is the AM's own text (typed via "Not listed", or
 *    extracted and never resolved), and a live `target_interest` is a
 *    decision already made. Auto-fill fills untouched fields only —
 *    overwriting either is data loss by helpfulness.
 *
 * Two or more options still arrive with nothing selected: the workflow
 * must not choose which mortgagee a replacing or destructive change
 * applies to.
 */
export function interestAutoPickPatch(
  nextValues: Record<string, unknown>,
  today?: Date,
): Record<string, unknown> {
  if (!readMovedPolicyNumber(nextValues)) {
    return {};
  }
  if (readMovedKey(nextValues, "target_interest")) return {};
  const name = nextValues.old_mortgagee_name;
  if (typeof name === "string" && name.length > 0) return {};
  const options = readInterestOptionsForSelection(nextValues, today);
  if (options.length !== 1) return {};
  return { target_interest: options[0], old_mortgagee_name: options[0].name };
}

/** The sentinel value for the "not listed" escape hatch. */
export const NOT_LISTED = "__not_listed__";
